// 2026-08-31 - FormuLearning - 학습 대상 2단계 선택 (이슈 #58 S2/#65, M84-34)
// 파일 위치: frontend/src/features/step4/LearningScopeSelector.tsx
// 목적: BO 학습 관측치를 **선택된 Round·Batch 집합**으로 한정한다(BR-RND-07).
//       종전은 프로젝트 관측치 전량을 무조건 썼다.
//
// 선택은 **라운드를 고르고 그 안의 배치를 고르는** 두 단계이며 **기본은 전체 포함이고
// 빼는 방식**이다. 그래서 이 컴포넌트는 체크를 켜는 도구가 아니라 **끄는 도구**다 —
// 아무것도 건드리지 않은 사용자는 전량 학습을 받는다.
//
// ⚠️ 라운드를 **전부 끄는 것은 막는다.** 서버는 빈 `round_ids` 를 "전체 포함" 으로 읽으므로
//    (BR-RND-07), 전부 끈 화면이 전량 학습을 부르는 뒤집힌 결과가 난다. 화면이 그 자리에서
//    막는 편이 서버 규약을 바꾸는 것보다 안전하다.

import { useState } from 'react'
import type { LotBatch } from '../../api/lot'
import { roundDisplayName, type RoundListItem } from '../../api/round'

interface Props {
  rounds: RoundListItem[]
  batches: LotBatch[]
  /** 학습에 포함할 라운드. 기본은 전 라운드다. */
  selectedRoundIds: string[]
  /** 그 안에서 뺀 배치. 기본은 빈 배열(전체 포함)이다. */
  excludedBatchIds: string[]
  onChangeRounds: (ids: string[]) => void
  onChangeBatches: (ids: string[]) => void
  disabled?: boolean
}

export default function LearningScopeSelector({
  rounds, batches, selectedRoundIds, excludedBatchIds,
  onChangeRounds, onChangeBatches, disabled,
}: Props) {
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  const roundSet = new Set(selectedRoundIds)
  const dropped = new Set(excludedBatchIds)
  const byRound = (rid: string) => batches.filter((b) => b.round_id === rid)

  // 요약은 **실제로 학습에 들어갈 것**을 센다 — 이미 제외된 배치(BR-S4-L02~L04)는
  // 여기서 다시 빼지 않아도 학습에 안 들어가므로 셈에서 제한다.
  const inScope = batches.filter(
    (b) => b.round_id && roundSet.has(b.round_id) && !dropped.has(b.id) && !b.is_excluded)

  const toggleRound = (rid: string) => {
    if (roundSet.has(rid)) {
      if (roundSet.size <= 1) return    // 마지막 하나는 끄지 않는다(위 ⚠️)
      onChangeRounds(selectedRoundIds.filter((x) => x !== rid))
    } else {
      onChangeRounds([...selectedRoundIds, rid])
    }
  }

  const toggleBatch = (bid: string) => {
    onChangeBatches(dropped.has(bid)
      ? excludedBatchIds.filter((x) => x !== bid)
      : [...excludedBatchIds, bid])
  }

  return (
    <div className="mb-2" data-testid="learning-scope">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          data-testid="learning-scope-toggle"
          className="rounded border px-2 py-0.5 text-[10px] font-semibold text-text-body transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ borderColor: 'var(--border-soft)' }}
        >
          {open ? '▾' : '▸'} 학습 대상
        </button>
        <span className="text-[10px] text-text-muted" data-testid="learning-scope-summary">
          {rounds.length}개 라운드 중 <b>{roundSet.size}개</b> · 배치 <b>{inScope.length}건</b> 학습
          {excludedBatchIds.length > 0 && ` (배치 ${excludedBatchIds.length}건 제외)`}
        </span>
      </div>

      {open && (
        <div className="mt-1 rounded border px-2 py-1.5" style={{ borderColor: 'var(--border-soft)' }}>
          <p className="mb-1 text-[10px] text-text-muted">
            기본은 <b>전체 포함</b>입니다. 학습에서 빼고 싶은 라운드·배치의 체크를 끄십시오.
          </p>
          {rounds.map((r) => {
            const rb = byRound(r.id)
            const checked = roundSet.has(r.id)
            return (
              <div key={r.id} className="border-t py-1 first:border-t-0"
                   style={{ borderColor: 'var(--border-soft)' }}>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1 text-[11px] text-text-body">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled || (checked && roundSet.size <= 1)}
                      onChange={() => toggleRound(r.id)}
                      data-testid={`scope-round-${r.round_no}`}
                    />
                    {roundDisplayName(r)}
                  </label>
                  <span className="text-[10px] text-text-muted">배치 {rb.length}건</span>
                  {rb.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      data-testid={`scope-expand-${r.round_no}`}
                      className="text-[10px] text-accent-purple underline"
                    >
                      {expanded === r.id ? '배치 접기' : '배치 고르기'}
                    </button>
                  )}
                </div>
                {expanded === r.id && (
                  <div className="mt-1 flex flex-wrap gap-2 pl-4">
                    {rb.map((b) => (
                      <label key={b.id}
                             className={`flex items-center gap-1 text-[10px] ${b.is_excluded ? 'text-text-muted line-through' : 'text-text-body'}`}
                             title={b.is_excluded ? '이미 제외된 배치입니다(BR-S4-L02~L04)' : undefined}>
                        <input
                          type="checkbox"
                          checked={!dropped.has(b.id)}
                          disabled={disabled || !checked || b.is_excluded}
                          onChange={() => toggleBatch(b.id)}
                          data-testid={`scope-batch-${b.batch_code}`}
                        />
                        {b.batch_code}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
          {roundSet.size <= 1 && (
            <p className="mt-1 text-[10px] text-text-muted">
              라운드를 전부 끌 수는 없습니다 — 학습할 관측치가 남아야 합니다.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
